import React, {useMemo, useState} from 'react';
import {Link, usePage} from '@inertiajs/react';
import {Badge, Card, Container, Form, Table} from 'react-bootstrap';
import {b2bProductUrl} from '../../functions/shopUrls.js';
import {compareForSort} from '../../functions/sorting.js';
import SortableTh from '../../Components/SortableTh.jsx';

const STATUS_MAP = {
    pending: {label: 'Wartet', variant: 'secondary'},
    processing: {label: 'Läuft', variant: 'info'},
    completed: {label: 'Fertig', variant: 'success'},
    failed: {label: 'Fehler', variant: 'danger'},
};

const TRIGGER_MAP = {
    schedule: 'Automatisch (Zeitplan)',
    manual: 'Manuell',
    'dry-run': 'Testlauf (Dry-Run)',
};

// Bezeichnungen wie in der Lagerübersicht und im Bestandsbericht: der Zustand
// "out" heißt in der Oberfläche "In Zulauf".
const STOCK_STATE_MAP = {
    in: {label: 'Auf Lager', variant: 'success'},
    out: {label: 'In Zulauf', variant: 'warning'},
};

function formatTimestamp(value) {
    if (!value) {
        return '–';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '–';
    }
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    });
}

function formatDuration(ms) {
    if (ms === null || ms === undefined) {
        return '–';
    }
    if (ms < 1000) {
        return `${ms} ms`;
    }
    return `${(ms / 1000).toFixed(1)} s`;
}

function StockStateBadge({state}) {
    const cfg = STOCK_STATE_MAP[state] || {label: state ?? '?', variant: 'light'};
    return <Badge bg={cfg.variant}>{cfg.label}</Badge>;
}

// Spalten der Änderungsliste inkl. Typ (für die Sortierung) und Zugriff auf
// den zu vergleichenden Wert - Status-Spalten sortieren nach dem
// angezeigten Label, nicht nach dem rohen Status-Code.
const CHANGE_COLUMNS = [
    {key: 'sku', label: 'Artikelnummer', type: 'text', getValue: (change) => change.sku},
    {key: 'bezeichnung', label: 'Bezeichnung', type: 'text', getValue: (change) => change.bezeichnung},
    {key: 'from', label: 'Von', type: 'text', getValue: (change) => STOCK_STATE_MAP[change.from]?.label ?? change.from},
    {key: 'to', label: 'Nach', type: 'text', getValue: (change) => STOCK_STATE_MAP[change.to]?.label ?? change.to},
];

export default function B2bRunDetail() {
    const {run, changes} = usePage().props;
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    const handleSort = (key) => {
        if (key === sortKey) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const filteredChanges = useMemo(() => {
        const list = Array.isArray(changes) ? changes : [];
        const term = search.trim().toLowerCase();
        if (!term) {
            return list;
        }
        return list.filter((change) =>
            String(change.sku ?? '').toLowerCase().includes(term)
            || String(change.bezeichnung ?? '').toLowerCase().includes(term)
        );
    }, [changes, search]);

    const sortedChanges = useMemo(() => {
        if (!sortKey) {
            return filteredChanges;
        }
        const column = CHANGE_COLUMNS.find((c) => c.key === sortKey);
        if (!column) {
            return filteredChanges;
        }
        const factor = sortDir === 'asc' ? 1 : -1;
        return [...filteredChanges].sort(
            (a, b) => factor * compareForSort(column.getValue(a), column.getValue(b), column.type)
        );
    }, [filteredChanges, sortKey, sortDir]);

    const statusCfg = STATUS_MAP[run.status] || {label: run.status, variant: 'light'};
    const snapshotDetails = run.snapshot_details || {};
    const snapshotWarnings = snapshotDetails.warnings || [];
    const warehouseDetails = Object.values(snapshotDetails.warehouses || {});

    return (
        <Container fluid className="pt-2">
            <div className="mt-1 mb-3 d-flex align-items-center gap-3">
                <h1 className="mb-0">Lauf-Details – B2B-Lagerverfügbarkeit</h1>
            </div>

            <div className="mb-3">
                <Link href="/stock/b2b-availability" className="btn btn-outline-secondary btn-sm">
                    ← Zurück zum Log
                </Link>
            </div>

            <Card className="mb-4">
                <Card.Body>
                    <div className="row row-cols-2 row-cols-md-6 g-3">
                        <div>
                            <div className="text-muted small">Zeitpunkt</div>
                            <div>{formatTimestamp(run.started_at)}</div>
                        </div>
                        <div>
                            <div className="text-muted small">Auslöser</div>
                            <div>{TRIGGER_MAP[run.trigger] || run.trigger || '–'}</div>
                        </div>
                        <div>
                            <div className="text-muted small">Status</div>
                            <div><Badge bg={statusCfg.variant}>{statusCfg.label}</Badge></div>
                        </div>
                        <div>
                            <div className="text-muted small">Geprüft</div>
                            <div>{run.articles_checked ?? '–'}</div>
                        </div>
                        <div>
                            <div className="text-muted small">Änderungen</div>
                            <div>{run.changes_count ?? '–'}</div>
                        </div>
                        <div>
                            <div className="text-muted small">Dauer</div>
                            <div>{formatDuration(run.duration_ms)}</div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {(snapshotWarnings.length > 0 || warehouseDetails.some((warehouse) => warehouse.zeroed?.length || warehouse.rechecked?.length)) && (
                <Card className="mb-4">
                    <Card.Body>
                        <Card.Title>Snapshot-Prüfung</Card.Title>
                        {snapshotWarnings.map((warning, index) => <div className="text-warning" key={index}>{warning}</div>)}
                        {warehouseDetails.map((warehouse) => (
                            <div className="mt-2" key={warehouse.warehouse_id}>
                                <strong>{warehouse.warehouse_name}:</strong>{' '}
                                {warehouse.zeroed?.length || 0} auf 0 gesetzt
                                {warehouse.rechecked?.length ? `, ${warehouse.rechecked.length} per Gegenprobe übernommen` : ''}.
                            </div>
                        ))}
                    </Card.Body>
                </Card>
            )}

            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Card.Title className="mb-0">Geänderte Artikel</Card.Title>
                        <Form.Control
                            type="search"
                            placeholder="Suche nach Artikelnummer oder Bezeichnung…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{maxWidth: 320}}
                        />
                    </div>

                    {filteredChanges.length === 0 ? (
                        <p className="text-muted mb-0">
                            {Array.isArray(changes) && changes.length > 0
                                ? 'Keine Artikel passen zur Suche.'
                                : 'Dieser Lauf hat keine Änderungen vorgenommen.'}
                        </p>
                    ) : (
                        <div style={{maxHeight: '70vh', overflowY: 'auto'}}>
                            <Table striped hover size="sm" className="mb-0">
                                <thead>
                                    <tr>
                                        {CHANGE_COLUMNS.map((column) => (
                                            <SortableTh
                                                key={column.key}
                                                column={column}
                                                sortKey={sortKey}
                                                sortDir={sortDir}
                                                onSort={handleSort}
                                            />
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedChanges.map((change, index) => (
                                        <tr key={`${change.sku ?? 'row'}-${index}`}>
                                            <td className="text-nowrap">
                                                {change.sku ? (
                                                    <a
                                                        href={b2bProductUrl(change.sku)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Im B2B-Shop suchen (b2b.molotow.com)"
                                                    >
                                                        {change.sku}
                                                    </a>
                                                ) : '–'}
                                            </td>
                                            <td>{change.bezeichnung ?? '–'}</td>
                                            <td><StockStateBadge state={change.from}/></td>
                                            <td><StockStateBadge state={change.to}/></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    <div className="text-muted small mt-2">
                        {filteredChanges.length} von {Array.isArray(changes) ? changes.length : 0} Änderungen angezeigt.
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
}
